import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Site, VismaIntegrationSettings as VismaIntegrationSettingsType } from '@/types';
import { apiService } from '@/utils/api';

const VismaIntegrationSettings: React.FC = () => {
  const [formData, setFormData] = useState({
    site_id: '',
    update_stock: false,
    update_stock_latest_poll_time: '',
    update_invoice_payment_status_latest_poll_time: '',
    customer_sync_latest_poll_time: '',
  });

  const [editingSettings, setEditingSettings] = useState<VismaIntegrationSettingsType | null>(null);
  const [siteId, setSiteId] = useState<number | null>(null);
  const queryClient = useQueryClient();

  // Fetch sites
  const { data: sites = [] } = useQuery<Site[]>('sites', apiService.getSites);

  // Auto-select first site when sites are loaded
  useEffect(() => {
    if (sites.length > 0 && !siteId) {
      setSiteId(sites[0].id);
      setFormData(prev => ({
        ...prev,
        site_id: sites[0].id.toString(),
      }));
    }
  }, [sites, siteId]);

  // Fetch integration settings
  const { isLoading } = useQuery<VismaIntegrationSettingsType>(
    ['vismaIntegrationSettings', siteId],
    () => apiService.getVismaIntegrationSettings(siteId!),
    {
      enabled: !!siteId,
      onSuccess: (data: VismaIntegrationSettingsType) => {
        // Only set editingSettings if we have actual data (not empty/null)
        if (data && data.id) {
          setEditingSettings(data);
          setFormData({
            site_id: data.site_id.toString(),
            update_stock: data.update_stock || false,
            update_stock_latest_poll_time: data.update_stock_latest_poll_time ? 
              new Date(data.update_stock_latest_poll_time).toISOString().slice(0, 16) : '',
            update_invoice_payment_status_latest_poll_time: data.update_invoice_payment_status_latest_poll_time ? 
              new Date(data.update_invoice_payment_status_latest_poll_time).toISOString().slice(0, 16) : '',
            customer_sync_latest_poll_time: data.customer_sync_latest_poll_time ? 
              new Date(data.customer_sync_latest_poll_time).toISOString().slice(0, 16) : '',
          });
        } else {
          // No existing data, clear editingSettings for POST request
          setEditingSettings(null);
          setFormData({
            site_id: siteId!.toString(),
            update_stock: false,
            update_stock_latest_poll_time: '',
            update_invoice_payment_status_latest_poll_time: '',
            customer_sync_latest_poll_time: '',
          });
        }
      },
    }
  );

  const createMutation = useMutation(
    (data: VismaIntegrationSettingsType) => apiService.createVismaIntegrationSettings(siteId!, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['vismaIntegrationSettings', siteId]);
        alert('Visma Integration Settings created successfully!');
      },
      onError: () => {
        alert('Failed to create Visma Integration Settings');
      },
    }
  );

  const updateMutation = useMutation(
    (data: VismaIntegrationSettingsType) => apiService.updateVismaIntegrationSettings(siteId!, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['vismaIntegrationSettings', siteId]);
        alert('Visma Integration Settings updated successfully!');
      },
      onError: () => {
        alert('Failed to update Visma Integration Settings');
      },
    }
  );

  const deleteMutation = useMutation(
    () => apiService.deleteVismaIntegrationSettings(siteId!),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['vismaIntegrationSettings', siteId]);
        setEditingSettings(null);
        setFormData({
          site_id: '',
          update_stock: false,
          update_stock_latest_poll_time: '',
          update_invoice_payment_status_latest_poll_time: '',
          customer_sync_latest_poll_time: '',
        });
        alert('Visma Integration Settings deleted successfully!');
      },
      onError: () => {
        alert('Failed to delete Visma Integration Settings');
      },
    }
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };


  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const data: VismaIntegrationSettingsType = {
      site_id: parseInt(formData.site_id),
      update_stock: formData.update_stock,
      update_stock_latest_poll_time: formData.update_stock_latest_poll_time || undefined,
      update_invoice_payment_status_latest_poll_time: formData.update_invoice_payment_status_latest_poll_time || undefined,
      customer_sync_latest_poll_time: formData.customer_sync_latest_poll_time || undefined,
    };

    if (editingSettings) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete these settings?')) {
      deleteMutation.mutate();
    }
  };

  if (isLoading && siteId) {
    return (
      <div className="container mx-auto p-6">
        <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded">
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="section-title">Visma Integration Settings</h1>
      
      {/* Site Selection */}
      <div className="mb-6">
        <label className="form-label">Site:</label>
        <select
          value={siteId || ''}
          onChange={(e) => {
            const newSiteId = parseInt(e.target.value);
            setSiteId(newSiteId);
            setEditingSettings(null);
            setFormData({
              site_id: e.target.value,
              update_stock: false,
              update_stock_latest_poll_time: '',
              update_invoice_payment_status_latest_poll_time: '',
              customer_sync_latest_poll_time: '',
            });
          }}
          className="form-input"
          disabled={isLoading}
        >
          {sites.map((site: Site) => (
            <option key={site.id} value={site.id}>
              {site.url}
            </option>
          ))}
        </select>
      </div>

      {/* Integration Settings Form */}
      <div className="card">
        <div className="card-body">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    name="update_stock"
                    checked={formData.update_stock}
                    onChange={handleInputChange}
                    className="form-checkbox"
                    disabled={isLoading}
                  />
                  <span className="form-label">Update Products Visma→Woo</span>
                </label>
              </div>

              <div>
                <label htmlFor="update_stock_latest_poll_time" className="form-label">
                  Latest time Update Stock Visma→Woo
                </label>
                <input
                  type="datetime-local"
                  id="update_stock_latest_poll_time"
                  name="update_stock_latest_poll_time"
                  value={formData.update_stock_latest_poll_time}
                  onChange={handleInputChange}
                  className="form-input"
                  disabled={isLoading}
                />
              </div>

              <div>
                <label htmlFor="update_invoice_payment_status_latest_poll_time" className="form-label">
                  Latest time Invoice Payment Status read
                </label>
                <input
                  type="datetime-local"
                  id="update_invoice_payment_status_latest_poll_time"
                  name="update_invoice_payment_status_latest_poll_time"
                  value={formData.update_invoice_payment_status_latest_poll_time}
                  onChange={handleInputChange}
                  className="form-input"
                  disabled={isLoading}
                />
              </div>

              <div>
                <label htmlFor="customer_sync_latest_poll_time" className="form-label">
                  Latest time Customer read
                </label>
                <input
                  type="datetime-local"
                  id="customer_sync_latest_poll_time"
                  name="customer_sync_latest_poll_time"
                  value={formData.customer_sync_latest_poll_time}
                  onChange={handleInputChange}
                  className="form-input"
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="flex justify-end pt-6 space-x-3">
              <button
                type="submit"
                className="px-6 py-2 text-white rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50"
                style={{ backgroundColor: 'hsl(15 100% 52%)' }}
                disabled={isLoading || createMutation.isLoading || updateMutation.isLoading}
              >
                {createMutation.isLoading || updateMutation.isLoading ? 'Saving...' : (editingSettings ? 'Update' : 'Save')}
              </button>
              
              {editingSettings && (
                <button
                  type="button"
                  onClick={handleDelete}
                  className="bg-red-600 text-white px-6 py-2 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
                  disabled={isLoading || deleteMutation.isLoading}
                >
                  {deleteMutation.isLoading ? 'Deleting...' : 'Delete'}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default VismaIntegrationSettings;
